import React, { useState, useEffect, useRef } from "react";
import { View, Text, StyleSheet, Pressable, TextInput, Image, Dimensions } from "react-native";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import { getAuth, signInAnonymously } from "firebase/auth";
import SharedModal from './SharedModal';
import genericUserPhoto from '../assets/generic.jpg'; 
import ReactDOM from 'react-dom';
import { useUser } from './UserContext'; 

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width < 1440;
const isLargeDesktop = width >= 1440;

const fontFamilies = {
  Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

const RSVPWidgets = ({ 
  card, 
  isOwner, 
  isVisitorView, 
  widgetIndex, 
  currentUserData, 
  pageOwner, 
  setHasChanges,
  rsvp,
  setRSVP 
}) => {
  // Get currentUser from useUser hook to access Firebase auth email
  const { currentUser } = useUser();
  
  // Helper function to get user's email (prioritize Firestore email, fallback to Firebase auth email)
  // This fixes the issue where some admin accounts don't have an email field in Firestore
  // but do have a Firebase authentication email
  const getUserEmail = () => {
    const firestoreEmail = currentUserData?.email;
    const authEmail = currentUser?.email;
    const email = firestoreEmail || authEmail;
    return email ? email.toLowerCase() : null;
  };
  
  // RSVP-related states
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ firstName: "", lastName: "", email: "" });
  const [showGuestInfoModal, setShowGuestInfoModal] = useState(false);
  const [tempRSVPSelection, setTempRSVPSelection] = useState(null);
  // RSVP responses modal state
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [activeRSVPTab, setActiveRSVPTab] = useState('Going');
  // RSVP user info cache
  const [userInfoCache, setUserInfoCache] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const isMounted = useRef(true);
  const userListRef = useRef(null);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  // Effect to update RSVP state when card data changes
  useEffect(() => {
    const userEmail = getUserEmail();
    if (card.going?.includes(userEmail)) {
      setRSVP("Going");
    } else if (card.maybe?.includes(userEmail)) {
      setRSVP("Maybe");
    } else if (card.decline?.includes(userEmail)) {
      setRSVP("Decline");
    } else {
      setRSVP("");    
    }
  }, [card, currentUserData, currentUser, rsvp, setRSVP]);

  const handleRSVP = async (selection) => {
    if (!currentUserData) {
      setTempRSVPSelection(selection);
      setShowGuestInfoModal(true);
      return;
    }
    
    const userEmail = getUserEmail();
    
    // Prevent RSVP if user has no email and no name
    if (!userEmail && !currentUserData.firstName && !currentUserData.lastName) {
      alert('You must have an email or name to RSVP. Please update your profile.');
      return;
    }
    
    // Additional check: if no email at all, prevent RSVP
    if (!userEmail) {
      alert('Unable to determine user email. Please log in again and try RSVP.');
      return;
    }
    // If the user clicks the same RSVP, unselect it
    if (rsvp === selection) {
      try {
        await submitRSVP("", userEmail, currentUserData.firstName, currentUserData.lastName);
      } catch (error) {
        console.error("Error unselecting RSVP:", error);
        alert("Failed to update RSVP. Please try again.");
      }
      return;
    }
    try {
      await submitRSVP(selection, userEmail, currentUserData.firstName, currentUserData.lastName);
    } catch (error) {
      console.error("Error handling RSVP for logged-in user:", error);
      alert("Failed to update RSVP. Please try again.");
    }
  };

  const submitRSVP = async (selection, email, firstName, lastName) => {
    // Validate email parameter
    if (!email) {
      throw new Error("Email is required for RSVP");
    }
    
    // Check if user is logged in - use getUserEmail to get the correct user email
    const userEmail = getUserEmail();
    const isLoggedInUser = currentUserData && userEmail === email;
    
    const db = getFirestore();
    const cardRef = doc(db, "users", pageOwner.email);

    try {
      // For guest users, sign in anonymously to get write permissions
      if (!isLoggedInUser) {
        const auth = getAuth();
        await signInAnonymously(auth);
      }

      // Get the current card data
      const cardSnap = await getDoc(cardRef);
      if (!cardSnap.exists()) {
        console.error("User document does not exist for:", pageOwner.email);
        throw new Error("User document not found");
      }

      const cardData = cardSnap.data();
      const widgets = cardData.pages?.eventsPageConfig?.cards || [];
      
      // Validate widget index
      if (widgetIndex >= widgets.length || widgetIndex < 0) {
        console.error("Invalid widget index:", widgetIndex);
        throw new Error("Invalid widget index");
      }
      
      const currentWidget = widgets[widgetIndex];
      
      // Initialize RSVP arrays if they don't exist
      const going = currentWidget.going || [];
      const maybe = currentWidget.maybe || [];
      const decline = currentWidget.decline || [];

      let updatedGoing = [...going];
      let updatedMaybe = [...maybe];
      let updatedDecline = [...decline];

      // Remove user from all lists first
      if (isLoggedInUser) {
        updatedGoing = updatedGoing.filter(userEmail => userEmail !== email);
        updatedMaybe = updatedMaybe.filter(userEmail => userEmail !== email);
        updatedDecline = updatedDecline.filter(userEmail => userEmail !== email);
      } else {
        // For guest users, remove by email
        updatedGoing = updatedGoing.filter(entry => typeof entry === 'string' ? entry !== email : entry.email !== email);
        updatedMaybe = updatedMaybe.filter(entry => typeof entry === 'string' ? entry !== email : entry.email !== email);
        updatedDecline = updatedDecline.filter(entry => typeof entry === 'string' ? entry !== email : entry.email !== email);
      }

      // Only add user to a list if selection is not empty
      if (selection === "Going") {
        if (isLoggedInUser) {
          updatedGoing.push(email);
        } else {
          updatedGoing.push({
            firstName,
            lastName,
            email,
            timestamp: new Date().toISOString()
          });
        }
      } else if (selection === "Maybe") {
        if (isLoggedInUser) {
          updatedMaybe.push(email);
        } else {
          updatedMaybe.push({
            firstName,
            lastName,
            email,
            timestamp: new Date().toISOString()
          });
        }
      } else if (selection === "Decline") {
        if (isLoggedInUser) {
          updatedDecline.push(email);
        } else {
          updatedDecline.push({
            firstName,
            lastName,
            email,
            timestamp: new Date().toISOString()
          });
        }
      }

      // Update the current widget
      widgets[widgetIndex] = {
        ...currentWidget,
        going: updatedGoing,
        maybe: updatedMaybe,
        decline: updatedDecline,
        rsvp: selection,
      };

      // Update Firestore document with only the necessary fields
      await updateDoc(cardRef, {
        "pages.eventsPageConfig.cards": widgets
      });

      // Update local state
      setRSVP(selection);
      card.going = updatedGoing;
      card.maybe = updatedMaybe;
      card.decline = updatedDecline;
      setHasChanges(true);
      
      // Close modal if it was open
      setShowGuestInfoModal(false);
    } catch (error) {
      console.error("Error updating RSVP:", error);
      alert("Failed to update RSVP. Please try again.");
      throw error;
    }
  };

  const handleGuestInfoSubmit = async () => {
    if (!guestInfo.firstName || !guestInfo.lastName || !guestInfo.email) {
      alert("Please fill in all fields");
      return;
    }
    
    try {
      await submitRSVP(
        tempRSVPSelection,
        guestInfo.email,
        guestInfo.firstName,
        guestInfo.lastName
      );
      
      setGuestInfo({ firstName: "", lastName: "", email: "" });
      setTempRSVPSelection(null);
    } catch (error) {
      console.error("Error submitting guest RSVP:", error);
      alert("Failed to update RSVP. Please try again.");
    }
  };

  const getRSVPList = (type) => {
    const arr = card[type]?.length ? card[type] : [];
    return arr.map(entry => typeof entry === 'string' ? { email: entry } : entry);
  };

  const getUserDisplayInfo = (user) => {
    if (user.firstName) {
      return user;
    }
    if (user.email && userInfoCache[user.email]) {
      return userInfoCache[user.email];
    }
    return user;
  };

  const hasAnyRSVP = (card.going && card.going.length) || (card.maybe && card.maybe.length) || (card.decline && card.decline.length);

  // Check if the list is scrollable and if we're not at the bottom
  const checkScroll = () => {
    const el = userListRef.current;
    if (!el) return;
    setShowBottomShadow(el.scrollHeight > el.clientHeight && el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  useEffect(() => {
    checkScroll();
    const el = userListRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [activeRSVPTab, showRSVPModal, card]);

  useEffect(() => {
    if (!showRSVPModal) return;
    const allUsers = ['going', 'maybe', 'decline']
      .flatMap(type => getRSVPList(type))
      .filter(user => user.email && !user.firstName && !userInfoCache[user.email]);
    if (allUsers.length === 0) return;

    setLoadingUsers(true);
    const db = getFirestore();
    Promise.all(allUsers.map(async (user) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.email));
        if (userDoc.exists()) {
          const data = userDoc.data();
          return { email: user.email, firstName: data.firstName, lastName: data.lastName, profilePictureURL: data.profilePictureURL };
        }
      } catch (e) {}
      return { email: user.email };
    })).then(results => {
      setUserInfoCache(prev => {
        const updated = { ...prev };
        results.forEach(user => { if (user.email) updated[user.email] = user; });
        return updated;
      });
      setLoadingUsers(false);
    });
  }, [showRSVPModal, activeRSVPTab, card]);

  const renderRSVPWidget = () => {
    const effectiveIsOwner = isOwner && !isVisitorView;
    
    return (
      <View style={[styles.rsvpContainer, { alignItems: 'center' }]}>
        <View style={{ width: '100%', alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 8 }}>
            <Pressable onPress={() => handleRSVP("Going")} style={[rsvp === "Going" ? styles.selectedButtonGreen : styles.RSVPbutton, styles.widgetShadow]}>
              <Text style={styles.RSVPButtonText}>Going</Text>
              <Text style={styles.rsvpCount}>{card.going?.length || 0}</Text>
            </Pressable>
            <Pressable onPress={() => handleRSVP("Maybe")} style={[rsvp === "Maybe" ? styles.selectedButtonBeige : styles.RSVPbutton, styles.widgetShadow]}>
              <Text style={styles.RSVPButtonText}>Maybe</Text>
              <Text style={styles.rsvpCount}>{card.maybe?.length || 0}</Text>
            </Pressable>
            <Pressable onPress={() => handleRSVP("Decline")} style={[rsvp === "Decline" ? styles.selectedButtonRed : styles.RSVPbutton, styles.widgetShadow]}>
              <Text style={styles.RSVPButtonText}>Not Going</Text>
              <Text style={styles.rsvpCount}>{card.decline?.length || 0}</Text>
            </Pressable>
          </View>
          {/* Show button only if there are any responses, centered below the RSVP buttons, and only for owner */}
          {effectiveIsOwner && hasAnyRSVP ? (
            <Pressable style={{ backgroundColor: '#4c956c', padding: 10, borderRadius: 8, minWidth: 100, alignItems: 'center', marginTop: 8 }} onPress={() => setShowRSVPModal((prev) => !prev)}>
              <Text style={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>View RSVP Responses</Text>
            </Pressable>
          ) : null}
        </View>
        {/* RSVP Modal: fixed, centered popup with dark overlay, rendered in a portal, only for owner */}
        {effectiveIsOwner && showRSVPModal && ReactDOM.createPortal(
          <View style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.92)',
            zIndex: 99999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <View style={{ backgroundColor: '#222', borderRadius: 12, padding: 20, minWidth: 320, maxWidth: 400, width: '90%' }}>
              {/* Tab Buttons */}
              <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 16 }}>
                {['Going', 'Maybe', 'Decline'].map(tab => (
                  <Pressable
                    key={tab}
                    style={{
                      backgroundColor: activeRSVPTab === tab ? '#fefde2' : '#4c956c',
                      padding: 8,
                      borderRadius: 8,
                      marginHorizontal: 6,
                    }}
                    onPress={() => setActiveRSVPTab(tab)}
                  >
                    <Text style={{ color: activeRSVPTab === tab ? '#4c956c' : '#fff', fontWeight: 'bold' }}>
                      {tab === 'Decline' ? 'Not Going' : tab}
                    </Text>
                  </Pressable>
                ))}
              </View>
              {/* User List */}
              <View style={{ maxHeight: 300, overflow: 'auto', position: 'relative' }}>
                <div
                  ref={userListRef}
                  style={{ maxHeight: 300, overflow: 'auto', position: 'relative' }}
                >
                  {loadingUsers ? (
                    <Text style={{ color: '#fff', textAlign: 'center', marginTop: 20 }}>Loading...</Text>
                  ) : getRSVPList(activeRSVPTab.toLowerCase()).length === 0 ? (
                    <View style={{ width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Text style={{ color: '#fff', textAlign: 'center' }}>No responses yet.</Text>
                    </View>
                  ) : (
                    getRSVPList(activeRSVPTab.toLowerCase()).map((user, idx) => {
                      const info = getUserDisplayInfo(user);
                      return (
                        <View key={user.email || idx} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                          <Image
                            source={info.profilePictureURL ? { uri: info.profilePictureURL } : genericUserPhoto}
                            style={{ width: 40, height: 40, borderRadius: 20, marginRight: 12, backgroundColor: '#eee' }}
                          />
                          <Text style={{ color: '#fff', fontWeight: 'bold' }}>
                            {info.firstName || ''} {info.lastName || ''}
                          </Text>
                        </View>
                      );
                    })
                  )}
                </div>
                { (isMobile || isTablet) && showBottomShadow && (
                  <View style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 30,
                    background: 'linear-gradient(180deg, rgba(34,34,34,0) 0%, #222 100%)',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }} />
                )}
              </View>
              {/* Close Button */}
              <Pressable style={{ marginTop: 18, alignSelf: 'center', backgroundColor: '#ff4c4c', padding: 8, borderRadius: 8 }} onPress={() => setShowRSVPModal(false)}>
                <Text style={{ color: '#fff', fontWeight: 'bold' }}>Close</Text>
              </Pressable>
            </View>
          </View>,
          document.body
        )}
      </View>
    );
  };

  return (
    <>
      {renderRSVPWidget()}
      <SharedModal 
        isVisible={showGuestInfoModal}
        onClose={() => {
          setShowGuestInfoModal(false);
          setTempRSVPSelection(null);
          setGuestInfo({ firstName: "", lastName: "", email: "" });
        }}
        title="Please provide your information"
        message={
          <View style={styles.guestInfoContainer}>
            <TextInput
              value={guestInfo.firstName}
              onChangeText={(text) => setGuestInfo(prev => ({ ...prev, firstName: text }))}
              placeholder="First Name"
              style={styles.guestInfoInput}
              placeholderTextColor="#999"
            />
            <TextInput
              value={guestInfo.lastName}
              onChangeText={(text) => setGuestInfo(prev => ({ ...prev, lastName: text }))}
              placeholder="Last Name"
              style={styles.guestInfoInput}
              placeholderTextColor="#999"
            />
            <TextInput
              value={guestInfo.email}
              onChangeText={(text) => setGuestInfo(prev => ({ ...prev, email: text }))}
              placeholder="Email"
              style={styles.guestInfoInput}
              placeholderTextColor="#999"
            />
          </View>
        }
        confirmText="Submit"
        onConfirm={handleGuestInfoSubmit}
      />
    </>
  );
};

const styles = StyleSheet.create({
  widgetShadow: {
    shadowColor: "rgba(0, 0, 0, 0.5)",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  rsvpContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  rsvpButtonContainer: {
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
  },
  RSVPbutton: {
    paddingHorizontal: isMobile ? 10 : 10,
    paddingVertical: isMobile ? 10 : 10,
    backgroundColor: "#ccc",
    borderRadius: 5,
    marginVertical: isMobile ? 5 : 5,
    width: "50%",
    height: isMobile ? 30 : 50,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    marginRight: isMobile ? 5 : 20,
  },
  RSVPButtonText: {
    color: "#000",
    fontSize: isMobile ? 18 : 20,
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },
  selectedButtonGreen: {
    backgroundColor: "#4c956c",
    paddingHorizontal: isMobile ? "auto" : 10,
    paddingVertical: isMobile ? "auto" : 10,
    borderRadius: 5,
    marginVertical: isMobile ? 5 : 5,
    width: "40%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    marginRight: isMobile ? 5 : 20,
  },
  selectedButtonBeige: {
    backgroundColor: "#fefde2",
    paddingHorizontal: isMobile ? "auto" : 10,
    paddingVertical: isMobile ? "auto" : 10,
    borderRadius: 5,
    marginVertical: isMobile ? 5 : 5,
    width: "40%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    marginRight: isMobile ? 5 : 20,
  },
  selectedButtonRed: {
    backgroundColor: "#ff4c4c",
    paddingHorizontal: isMobile ? "auto" : 10,
    paddingVertical: isMobile ? "auto" : 10,
    borderRadius: 5,
    marginVertical: isMobile ? 5 : 5,
    width: "40%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    marginRight: isMobile ? 5 : 20,
  },
  rsvpCount: {
    position: "absolute",
    top: isMobile ? 2.5 : 5,
    right: isMobile ? 2.5 : 5, 
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: isMobile ? 2.5 : 5,
    fontSize: isMobile ? 12 : 16,
    fontWeight: "bold",
  },
  guestInfoContainer: {
    width: '100%',
    marginBottom: 15,
  },
  guestInfoInput: {
    width: '100%',
    padding: 12,
    marginBottom: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    fontSize: 16,
    fontFamily: 'Orpheus Pro',
    backgroundColor: '#fff',
    color: '#4c956c',
    textAlign: 'center',
  },
});

export default RSVPWidgets; 